import React, { useRef, useEffect } from 'react';
import './hrconsulting.css';
import { Users, TrendingUp, Heart, BarChart3, ArrowRight } from 'lucide-react';
import HR from './consulting.png';
import benefitsImage from './manconsulting.png';

const HRConsulting = () => {
  const scrollToSection = () => {
    const section = document.getElementById('target-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToContact = () => {
    window.location.href = '/contact';
  };

  const overviewTextRef = useRef(null);
  const approachRefs = useRef([]);
  const benefitsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('in-view');
        });
      },
      { threshold: 0.3 }
    );

    const elements = [
      overviewTextRef.current,
      benefitsRef.current,
      ...approachRefs.current
    ].filter(Boolean);

    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const approaches = [
    {
      icon: Users,
      title: 'Holistic Methodology',
      description: 'We integrate occupational health guidelines with data-driven insights to create a balanced, high-performing work environment. Our comprehensive approach ensures every aspect of workplace wellbeing is optimized for both productivity and mental health.'
    },
    {
      icon: TrendingUp,
      title: 'Technology Integration',
      description: 'Utilizing Whitematter\'s advanced AI-driven neurofeedback system, we deliver personalized feedback without the need for wearables. This technology empowers employees to self-optimize, fostering a culture of proactive wellbeing and sustained performance.'
    }
  ];

  const benefits = [
    'Data-driven insights for balanced workloads',
    'Boosted productivity and enhanced wellbeing',
    'Reduced stress and improved mental health',
    'Personalized feedback without wearables'
  ];

  return (
    <div className="hr-consulting-container">
      {/* Hero Section */}
      <section className="hr-consulting-hero">
        <div className="hr-consulting-hero-content">
          <h1 className="hr-consulting-hero-title">
            HR <span className="hr-consulting-hero-accent">Consulting</span>
          </h1>
          <p className="hr-consulting-hero-subtitle">
            Transforming Workspaces: Elevating Mental Health & Productivity
          </p>
          <div className="hr-consulting-hero-buttons">
            <button className="hr-consulting-btn hr-consulting-btn-primary" onClick={scrollToSection}>
              Learn More
              <ArrowRight className="hr-consulting-btn-icon" />
            </button>
            <button className="hr-consulting-btn hr-consulting-btn-secondary" onClick={goToContact}>
              Contact Us
              <ArrowRight className="hr-consulting-btn-icon" />
            </button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="hr-consulting-overview" id="target-section">
        <div className="hr-consulting-section-wrapper">
          <div className="hr-consulting-overview-grid">
            <div className="hr-consulting-overview-image">
              <img src={HR} alt="Consulting & Workplace Wellbeing" />
            </div>
            <div className="hr-consulting-overview-content" ref={overviewTextRef}>
              <h2 className="hr-consulting-overview-title">Optimizing Mental Health & Productivity</h2>
              <p className="hr-consulting-overview-description">
                Our consulting service integrates cutting-edge technology with strategic guidance to foster a healthier,
                more productive work environment. Leveraging Whitematter's AI-driven neurofeedback system, we empower organizations
                to enhance workplace wellbeing and drive performance—all without the need for wearables.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="hr-consulting-approach">
        <div className="hr-consulting-section-wrapper">
          <div className="hr-consulting-section-header">
            <h2 className="hr-consulting-section-title">Our Approach</h2>
            <p className="hr-consulting-section-subtitle">
              Comprehensive solutions that transform workplace wellbeing
            </p>
          </div>
          
          <div className="hr-consulting-approach-grid">
            {approaches.map((approach, index) => {
              const IconComponent = approach.icon;
              return (
                <div
                  key={index}
                  className="hr-consulting-approach-card"
                  ref={(el) => (approachRefs.current[index] = el)}
                >
                  <div className="hr-consulting-approach-icon">
                    <IconComponent strokeWidth={1.5} />
                  </div>
                  <h3 className="hr-consulting-approach-title">{approach.title}</h3>
                  <p className="hr-consulting-approach-description">{approach.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="hr-consulting-benefits">
        <div className="hr-consulting-section-wrapper">
          <div className="hr-consulting-benefits-grid">
            <div className="hr-consulting-benefits-content" ref={benefitsRef}>
              <h2 className="hr-consulting-benefits-title">Benefits & Outcomes</h2>
              <div className="hr-consulting-benefits-list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="hr-consulting-benefit-item">
                    <div className="hr-consulting-benefit-number">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                    <p className="hr-consulting-benefit-text">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="hr-consulting-benefits-image">
              <img src={benefitsImage} alt="Benefits Illustration" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="hr-consulting-cta">
        <div className="hr-consulting-section-wrapper">
          <h2 className="hr-consulting-cta-title">Ready to Transform Your Workplace?</h2>
          <p className="hr-consulting-cta-subtitle">
            Our pricing is customized to the needs of your organization. Contact us for a personalized quote.
          </p>
          <button className="hr-consulting-btn hr-consulting-btn-primary" onClick={goToContact}>
            Contact Us
            <ArrowRight className="hr-consulting-btn-icon" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default HRConsulting;